import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Reveal } from "./Reveal";
import { SERVICE_OPTIONS, VEHICLE_OPTIONS } from "../data/content";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  preferred_vehicle: "",
  name: "",
  phone: "",
  email: "",
  date: "",
  time: "",
  service_type: "",
  description: "",
};

export const QuoteForm = () => {
  const { t } = useLang();
  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) =>
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const setField = (field, value) =>
    setFormData((p) => ({ ...p, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/quote-request`, formData);
      toast.success(t.form.toastSuccessTitle, { description: t.form.toastSuccessDesc });
      setFormData(initialState);
    } catch (err) {
      toast.error(t.form.toastErrTitle, { description: t.form.toastErrDesc });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls =
    "bg-[#1A1A1D] border-[#C5A059]/25 text-[#FDFBF7] placeholder:text-zinc-500 h-12 rounded-md focus-visible:ring-1 focus-visible:ring-[#C5A059] focus-visible:border-[#C5A059]";
  const selectCls =
    "bg-[#1A1A1D] border-[#C5A059]/25 text-[#FDFBF7] h-12 rounded-md focus:ring-1 focus:ring-[#C5A059]";
  const labelCls = "block text-xs uppercase tracking-[0.15em] text-[#C5A059] mb-2";

  return (
    <section
      id="preventivo"
      data-testid="quote-section"
      className="scroll-mt-20 py-24 md:py-32 bg-[#0A0A0C] border-t border-[#C5A059]/15"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <Reveal className="text-center">
          <span className="block text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">
            {t.form.overline}
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
            {t.form.title}
          </h2>
          <p className="mt-4 text-zinc-400 font-light">{t.form.subtitle}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={handleSubmit}
            data-testid="quote-form"
            className="mt-12 bg-[#141417] border border-[#C5A059]/25 p-7 md:p-10 space-y-6 shadow-[0_0_40px_rgba(0,0,0,0.4)]"
          >
            <div>
              <label className={labelCls}>{t.form.vehicle}</label>
              <Select
                value={formData.preferred_vehicle}
                onValueChange={(v) => setField("preferred_vehicle", v)}
                required
              >
                <SelectTrigger data-testid="quote-vehicle-select" className={selectCls}>
                  <SelectValue placeholder={t.form.vehiclePh} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/15 text-white">
                  {VEHICLE_OPTIONS.map((name) => (
                    <SelectItem key={name} value={name} className="focus:bg-white/10">
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className={labelCls}>{t.form.name}</label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t.form.namePh}
                required
                data-testid="quote-name-input"
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>{t.form.phone}</label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={t.form.phonePh}
                required
                data-testid="quote-phone-input"
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>{t.form.email}</label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t.form.emailPh}
                required
                data-testid="quote-email-input"
                className={inputCls}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className={labelCls}>{t.form.date}</label>
                <Input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  data-testid="quote-date-input"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>{t.form.time}</label>
                <Input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                  data-testid="quote-time-input"
                  className={inputCls}
                />
              </div>
            </div>

            <div>
              <label className={labelCls}>{t.form.service}</label>
              <Select
                value={formData.service_type}
                onValueChange={(v) => setField("service_type", v)}
                required
              >
                <SelectTrigger data-testid="quote-service-select" className={selectCls}>
                  <SelectValue placeholder={t.form.servicePh} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/15 text-white">
                  {SERVICE_OPTIONS.map((o, i) => (
                    <SelectItem key={o.value} value={o.value} className="focus:bg-white/10">
                      {t.form.options[i]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className={labelCls}>{t.form.description}</label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder={t.form.descPh}
                required
                rows={5}
                data-testid="quote-desc-textarea"
                className="bg-[#1A1A1D] border-[#C5A059]/25 text-[#FDFBF7] placeholder:text-zinc-500 rounded-md resize-none focus-visible:ring-1 focus-visible:ring-[#C5A059] focus-visible:border-[#C5A059]"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="quote-submit-btn"
              className="w-full bg-[#C5A059] text-[#0A0A0C] py-4 text-sm font-medium uppercase tracking-[0.15em] hover:bg-[#d8b877] transition-colors disabled:opacity-60"
            >
              {isSubmitting ? t.form.submitting : t.form.submit}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
